import { motion } from "framer-motion";
import { ArrowRight, TrendingDown, TrendingUp, Search, ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const ENGINE_NODES = [
    { label: "Creator Discovery", tone: "dark" },
    { label: "Affiliate Recruiting", tone: "light" },
    { label: "YouTube Partnerships", tone: "light" },
    { label: "UGC Machine", tone: "light" },
    { label: "Operating System", tone: "accent" },
];

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const onSeeHow = () => {
        if (location.pathname === "/") scrollTo("how-we-work");
        else navigate("/#how-we-work");
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-white"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-14 lg:gap-16 items-center">
                    {/* Left: copy */}
                    <div className="max-w-2xl">
                        <motion.h1
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            data-testid="hero-heading"
                            className="font-display font-extrabold text-[32px] leading-[1.15] sm:text-[44px] sm:leading-[1.15] md:text-[48px] md:leading-[1.15] lg:text-[56px] lg:leading-[1.15] tracking-[-0.025em] text-[#0A0A0A] text-balance"
                        >
                            Your productivity app is a thousand creators away from market dominance.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            data-testid="hero-subheading"
                            className="mt-7 max-w-xl text-[17px] md:text-lg text-gray-600 leading-relaxed text-pretty"
                        >
                            Paid ads get more expensive. SEO gets more competitive. I help productivity apps build an in-house creator engine that compounds over time, bringing together affiliates, YouTube partnerships, and UGC into one repeatable growth system.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <button
                                data-testid="hero-cta-primary"
                                onClick={() => scrollTo("final-cta")}
                                className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] hover:bg-black text-white h-12 px-6 text-sm font-semibold shadow-[0_16px_36px_-14px_rgba(0,0,0,0.35)] transition-all hover:-translate-y-[1px]"
                            >
                                Book a Strategy Call
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                data-testid="hero-cta-secondary"
                                onClick={onSeeHow}
                                className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#F5F5F5] text-[#0A0A0A] h-12 px-6 text-sm font-semibold border border-black/10 transition-all"
                            >
                                See How It Works
                            </button>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="mt-5 text-xs text-gray-500"
                        >
                            Free · 30 minutes · No sales pitch
                        </motion.p>
                    </div>

                    {/* Right: creator engine diagram */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        data-testid="hero-engine-visual"
                        className="relative"
                    >
                        <div className="relative rounded-3xl border border-black/[0.08] bg-[#FAFAFA] p-6 md:p-8">
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-gray-500">
                                    Creator engine
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#C6F432]" />
                                    Compounding
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {ENGINE_NODES.map((n) => (
                                    <div
                                        key={n.label}
                                        className={`rounded-xl px-4 py-4 text-[13.5px] font-semibold leading-[1.65] ${
                                            n.tone === "dark"
                                                ? "bg-[#0A0A0A] text-white"
                                                : n.tone === "accent"
                                                ? "bg-[#C6F432] text-[#0A0A0A]"
                                                : "bg-white text-[#0A0A0A] border border-black/[0.08]"
                                        }`}
                                    >
                                        {n.label}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 rounded-xl bg-white border border-black/[0.08] p-4">
                                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-gray-400 font-bold">
                                    <span>Month 1</span>
                                    <span>Month 2</span>
                                    <span>Month 3</span>
                                </div>
                                <div className="mt-3 h-1.5 bg-black/5 rounded-full overflow-hidden">
                                    <div className="h-full w-full bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A] to-[#C6F432]" />
                                </div>
                                <p className="mt-3 text-[13px] text-gray-500 leading-relaxed">
                                    I build the entire system with you for 3 months. Then you take it in-house and keep growing without me.
                                </p>
                                <div className="mt-4 pt-4 border-t border-black/[0.06]">
                                    <p className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-gray-400">
                                        Ripple effect
                                    </p>
                                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                                        <OutcomeChip icon={TrendingDown} label="Lower CAC" />
                                        <OutcomeChip icon={TrendingUp} label="Higher ROI" />
                                        <OutcomeChip icon={Search} label="Branded search" />
                                        <OutcomeChip icon={ArrowUpRight} label="Higher conversion" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;

const OutcomeChip = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAFAFA] border border-black/[0.06] px-2.5 h-7 text-[11.5px] font-semibold text-[#0A0A0A]">
        <Icon className="h-3 w-3" strokeWidth={2.2} />
        {label}
    </span>
);
